import { Link } from "react-router-dom";
import { Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { formatCents, type Expense } from "../../../lib/sales";
import { StatusPill, fmtDate } from "../../ui";
import { ExpenseRowMenu } from "./ExpenseRowMenu";

/**
 * Category, date, vendor, client, project, amount, status and the menu - eight
 * tracks. The constant lives here, with the row that draws it, and the list head
 * imports it: two separate track lists drift apart the first time somebody adds
 * a column, and then the numbers sit under the wrong heading.
 *
 * Project is the newcomer, and the widths around it were shaved rather than the
 * row widened, because the column is not decoration: `Expense.projectId` is what
 * a project's spend figure is summed from, so a list that cannot show it is a
 * list where nobody can check the figure.
 */
export const COLS = "minmax(0,1.9fr) .85fr 1fr 1.1fr 1.1fr .9fr 1fr 44px";

/** Settled once the expense has produced an invoice, waiting while it is still
 *  owed to somebody, neutral when it was never meant to be passed on.
 *
 *  Exported because the project page draws its own narrower expense row and has
 *  to reach the same verdict: a second copy of this ladder would let the same
 *  expense read as "billable" on one screen and "rebilled" on the other the
 *  first time somebody adds a state to one of them. */
export function statusOf(expense: Expense): { tone: "ok" | "wt" | "mut"; key: string } {
  if (expense.invoicedId) return { tone: "ok", key: "expenses.billed" };
  if (expense.billable) return { tone: "wt", key: "expenses.billable" };
  return { tone: "mut", key: "expenses.notBillable" };
}

export function ExpenseRow({
  expense,
  projectName,
  canManage,
  onOpenInvoice,
  onBill,
  onEdit,
  onDelete,
}: {
  expense: Expense;
  /**
   * The linked project's name, looked up by the list.
   *
   * Passed in rather than fetched here because the row has only the id: there
   * is no foreign key behind `Expense.projectId`, so the endpoint has no
   * relation to include and returns the bare string. The list already loads the
   * projects for its own filter, so one request answers every row.
   */
  projectName?: string;
  canManage: boolean;
  onOpenInvoice: (invoiceId: string) => void;
  onBill: (expense: Expense) => void;
  onEdit: (expense: Expense) => void;
  onDelete: (expense: Expense) => void;
}) {
  const t = useT();
  const status = statusOf(expense);

  return (
    // No row-wide link here: an expense has no page of its own, so nothing
    // stretches an ::after over the row. The one record worth reaching from a
    // row is the client, and that is a real <Link> in its own column - which is
    // also why the row is a div ending in a menu button rather than an anchor.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        {/* A tinted disc rather than an avatar: initials made out of a category
            name would read as a person who does not exist. */}
        <span className="crm-av crm-av-soft" aria-hidden>
          <Receipt className="size-4" />
        </span>
        <span className="min-w-0">
          <span className="crm-prow-n crm-trunc block">{expense.category}</span>
          <span className="crm-prow-s crm-trunc block">{expense.description ?? "-"}</span>
        </span>
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(expense.spentAt)}</span>
      <span className="crm-mut crm-trunc text-[12.5px]">{expense.vendor ?? "-"}</span>

      <span className="crm-trunc text-[12.5px]">
        {expense.client ? (
          <Link to={`/clients/${expense.client.id}`} className="crm-trunc block hover:underline">
            {expense.client.name}
          </Link>
        ) : (
          <span className="crm-mut">-</span>
        )}
      </span>

      <span className="crm-trunc text-[12.5px]">
        {expense.projectId ? (
          // The name when the list knows it, the neutral word when it does not:
          // the lookup covers the first hundred projects, and a raw cuid in a
          // table cell would be worse than a link that says what it leads to.
          // Either way the link goes to the project, which is what the cell is
          // for - checking that this cost landed where somebody thinks it did.
          <Link to={`/projects/${expense.projectId}`} className="crm-trunc block hover:underline">
            {projectName ?? t("expenses.project")}
          </Link>
        ) : (
          <span className="crm-mut">-</span>
        )}
      </span>

      {/* What was spent net of VAT - the same figure the strip above adds up,
          and the figure a rebilled invoice line is written for. */}
      <span className="crm-tnum text-[13.25px] font-bold">
        {formatCents(expense.amountCents, expense.currency)}
      </span>

      <span>
        <StatusPill tone={status.tone}>{t(status.key)}</StatusPill>
      </span>

      <ExpenseRowMenu
        expense={expense}
        canManage={canManage}
        onOpenInvoice={onOpenInvoice}
        onBill={onBill}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}
